/**
 * Promotions & Discounts Page
 * V6.15: Comprehensive promotion management and analytics
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Skeleton,
  Grid,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Refresh as RefreshIcon,
  LocalFireDepartment as FireIcon,
  RocketLaunch as RocketIcon,
  FiberNew as NewIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import apiService from '../../services/api';
import { safeMetric, safeCount } from '../../utils/safeMetric';

interface CouponData {
  id: string;
  stripeCouponId: string;
  code: string;
  name: string;
  description: string | null;
  discountType: string;
  percentOff: number | null;
  amountOff: number | null;
  currency: string;
  duration: string;
  durationInMonths: number | null;
  maxRedemptions: number | null;
  currentRedemptionCount: number;
  validFrom: string;
  validUntil: string | null;
  isActive: boolean;
  createdAt: string;
}

interface ABTestData {
  id: string;
  name: string;
  description: string | null;
  status: string;
  startDate: string | null;
  endDate: string | null;
  variantCount: number;
  totalImpressions: number;
  totalConversions: number;
  conversionRate: number;
}

interface SuggestedCampaign {
  id: string;
  name: string;
  description: string;
  type: string;
  estimatedImpact: string;
  status: string;
}

interface PromotionsData {
  overview: {
    totalCoupons: number;
    activeCoupons: number;
    totalRedemptions: number;
    totalDiscountGiven: number;
    avgDiscount: number;
  };
  launch50: {
    totalRedemptions: number;
    totalDiscountGiven: number;
    targetSubs: number;
    endsDate: string;
    isActive: boolean;
  };
  coupons: CouponData[];
  abTests: ABTestData[];
  suggestedCampaigns: SuggestedCampaign[];
}

const defaultData: PromotionsData = {
  overview: { totalCoupons: 0, activeCoupons: 0, totalRedemptions: 0, totalDiscountGiven: 0, avgDiscount: 0 },
  launch50: { totalRedemptions: 0, totalDiscountGiven: 0, targetSubs: 10000, endsDate: '2026-06-27', isActive: false },
  coupons: [],
  abTests: [],
  suggestedCampaigns: [],
};

const PromotionsDiscountsPage: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<PromotionsData>(defaultData);
  const [loading, setLoading] = useState(true);
  const [ftSegments, setFtSegments] = useState<{ id: string; name: string; count: number; description: string }[]>([]);

  useEffect(() => {
    loadData();
    loadFreeTierSegments();
  }, []);

  const loadData = async () => {
    try {
      const response = await apiService.get('/api/admin/promotions');
      if (response.success && response.data) {
        setData(response.data as any);
      }
    } catch (error) {
      console.error('Failed to load promotions data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadFreeTierSegments = async () => {
    try {
      const res = await apiService.getFreeTierSegments();
      if (res?.segments) {
        setFtSegments(res.segments.filter((s: any) => ['hot_leads', 'upgrade_ready', 'recent_joiners'].includes(s.id)));
      }
    } catch (e) { /* ignore */ }
  };

  const copyCode = (code: string) => {
    navigator.clipboard.writeText(code);
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="rectangular" height={48} sx={{ mb: 2, borderRadius: 2 }} />
        <Skeleton variant="rectangular" height={120} sx={{ mb: 2, borderRadius: 2 }} />
        <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, bgcolor: '#F9FAFB', minHeight: '100vh' }}>
      {/* Breadcrumb */}
      <Typography
        sx={{
          fontFamily: "'Montserrat', sans-serif",
          fontSize: '11px',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.8px',
          color: '#6B7280',
          mb: 1,
        }}
      >
        MEMBERS & SUBSCRIPTIONS{' '}
        <Box component="span" sx={{ mx: 0.5 }}>&rarr;</Box>{' '}
        <Box component="span" sx={{ color: '#1A5F7A' }}>PROMOTIONS & DISCOUNTS</Box>
      </Typography>

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography
            sx={{
              fontFamily: "'Playfair Display', serif",
              fontSize: '30px',
              fontWeight: 700,
              color: '#111827',
              lineHeight: 1.2,
            }}
          >
            Promotions & Discounts
          </Typography>
          <Typography
            sx={{
              fontFamily: "'Montserrat', sans-serif",
              fontSize: '13px',
              color: '#6B7280',
              mt: 0.5,
            }}
          >
            Manage coupons, LAUNCH50 campaign, A/B tests, and promotional strategies
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={loadData}
            sx={{
              fontFamily: "'Montserrat', sans-serif",
              fontSize: '12px',
              fontWeight: 600,
              textTransform: 'none',
              borderColor: '#D1D5DB',
              color: '#374151',
            }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            size="small"
            sx={{
              fontFamily: "'Montserrat', sans-serif",
              fontSize: '12px',
              fontWeight: 600,
              textTransform: 'none',
              bgcolor: '#1A5F7A',
              '&:hover': { bgcolor: '#154D62' },
            }}
          >
            Create Coupon
          </Button>
        </Box>
      </Box>

      {/* LAUNCH50 Hero Banner */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0f3d50, #1A5F7A)',
          borderRadius: '14px',
          p: '24px',
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Box
            sx={{
              background: '#D4AC0D',
              color: '#3d2e00',
              px: '14px',
              py: '6px',
              borderRadius: '8px',
              fontFamily: "'Montserrat', sans-serif",
              fontSize: '16px',
              fontWeight: 800,
            }}
          >
            LAUNCH50
          </Box>
          <Box>
            <Typography
              sx={{
                fontFamily: "'Montserrat', sans-serif",
                fontSize: '16px',
                fontWeight: 700,
                color: '#fff',
              }}
            >
              Q1 2026 Launch Campaign
            </Typography>
            <Typography
              sx={{
                fontFamily: "'Montserrat', sans-serif",
                fontSize: '12px',
                color: 'rgba(255,255,255,0.7)',
              }}
            >
              50% off ALL annual plans for first year &middot; Standard $49.99 &middot; Premium $74.99 &middot; Legacy Creator $119.99 &middot; Vault Std $14.99 &middot; Vault Premium $34.99 &middot; Mar 27 &ndash; Jun 27, 2026
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 4 }}>
          {[
            { value: safeCount(data.launch50.totalRedemptions), label: 'Redemptions' },
            { value: `$${data.launch50.totalDiscountGiven.toFixed(0)}`, label: 'Discount Given' },
            { value: `${(data.launch50.targetSubs / 1000).toFixed(0)}K`, label: 'Target' },
            { value: data.launch50.isActive ? 'Active' : 'Ended', label: 'Status' },
          ].map((stat) => (
            <Box key={stat.label}>
              <Typography
                sx={{
                  fontFamily: "'Montserrat', sans-serif",
                  fontSize: '24px',
                  fontWeight: 800,
                  color: '#D4AC0D',
                }}
              >
                {stat.value}
              </Typography>
              <Typography
                sx={{
                  fontFamily: "'Montserrat', sans-serif",
                  fontSize: '10px',
                  fontWeight: 600,
                  color: 'rgba(255,255,255,0.6)',
                  textTransform: 'uppercase',
                }}
              >
                {stat.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* KPI Cards */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(5, 1fr)',
          gap: '14px',
          mb: 3,
        }}
      >
        {[
          { label: 'ACTIVE COUPONS', value: safeCount(data.overview.activeCoupons), border: '#1A5F7A' },
          { label: 'TOTAL REDEMPTIONS', value: safeCount(data.overview.totalRedemptions), border: '#10B981' },
          { label: 'DISCOUNT GIVEN', value: `$${data.overview.totalDiscountGiven.toFixed(2)}`, border: '#D4AC0D' },
          { label: 'AVG DISCOUNT', value: data.overview.avgDiscount > 0 ? `$${data.overview.avgDiscount.toFixed(2)}` : '\u2014', border: '#7c3aed' },
          { label: 'A/B TESTS', value: safeCount(data.abTests.length), border: '#0ea5e9' },
        ].map((kpi) => (
          <Paper
            key={kpi.label}
            sx={{
              p: '16px',
              borderRadius: '14px',
              borderTop: `3px solid ${kpi.border}`,
              border: '1px solid #e5e7eb',
            }}
          >
            <Typography
              sx={{
                fontFamily: "'Montserrat', sans-serif",
                fontSize: '9.5px',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.8px',
                color: '#6B7280',
                mb: 0.5,
              }}
            >
              {kpi.label}
            </Typography>
            <Typography
              sx={{
                fontFamily: "'Montserrat', sans-serif",
                fontSize: '24px',
                fontWeight: 800,
                color: '#111827',
              }}
            >
              {kpi.value}
            </Typography>
          </Paper>
        ))}
      </Box>

      {/* Free Tier Conversion Targeting */}
      {ftSegments.length > 0 && (
        <Paper sx={{ borderRadius: '14px', border: '1px solid #e5e7eb', mb: 3, p: 2 }}>
          <Typography
            sx={{
              fontFamily: "'Montserrat', sans-serif",
              fontSize: '13px',
              fontWeight: 700,
              color: '#111827',
              mb: 2,
            }}
          >
            Free Tier Conversion Targeting
          </Typography>
          <Grid container spacing={2}>
            {ftSegments.map((seg) => {
              const icons: Record<string, React.ReactNode> = {
                hot_leads: <FireIcon sx={{ color: '#ef4444' }} />,
                upgrade_ready: <RocketIcon sx={{ color: '#8b5cf6' }} />,
                recent_joiners: <NewIcon sx={{ color: '#0ea5e9' }} />,
              };
              return (
                <Grid item xs={12} sm={4} key={seg.id}>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      {icons[seg.id] || null}
                      <Box>
                        <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>{seg.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{safeCount(seg.count)} members</Typography>
                      </Box>
                    </Box>
                    <Button size="small" variant="outlined" onClick={() => navigate('/dashboard/communications/marketing/funnels')}>
                      Target
                    </Button>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Paper>
      )}

      {/* Coupon Table */}
      <Paper sx={{ borderRadius: '14px', border: '1px solid #e5e7eb', mb: 3, overflow: 'hidden' }}>
        <Box sx={{ p: 2, borderBottom: '1px solid #e5e7eb' }}>
          <Typography
            sx={{
              fontFamily: "'Montserrat', sans-serif",
              fontSize: '13px',
              fontWeight: 700,
              color: '#111827',
            }}
          >
            All Coupons
          </Typography>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#F9FAFB' }}>
                {['Code', 'Name', 'Discount', 'Duration', 'Redemptions', 'Status', 'Valid Until', 'Actions'].map((h) => (
                  <TableCell
                    key={h}
                    sx={{
                      fontFamily: "'Montserrat', sans-serif",
                      fontSize: '10px',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      color: '#6B7280',
                      letterSpacing: '0.5px',
                    }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.coupons.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4, color: '#6B7280' }}>
                    No coupons found. Create your first coupon to get started.
                  </TableCell>
                </TableRow>
              ) : (
                data.coupons.map((coupon) => (
                  <TableRow key={coupon.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography
                          sx={{
                            fontFamily: "'Montserrat', sans-serif",
                            fontSize: '12px',
                            fontWeight: 700,
                            color: '#1A5F7A',
                          }}
                        >
                          {coupon.code}
                        </Typography>
                        <Tooltip title="Copy code">
                          <IconButton size="small" onClick={() => copyCode(coupon.code)}>
                            <CopyIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '12px' }}>
                      {coupon.name}
                    </TableCell>
                    <TableCell sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '12px', fontWeight: 600 }}>
                      {coupon.percentOff ? `${coupon.percentOff}% off` : coupon.amountOff ? `$${coupon.amountOff} off` : '\u2014'}
                    </TableCell>
                    <TableCell sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '11px', color: '#6B7280' }}>
                      {coupon.duration}{coupon.durationInMonths ? ` (${coupon.durationInMonths}mo)` : ''}
                    </TableCell>
                    <TableCell sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '12px' }}>
                      {coupon.currentRedemptionCount}
                      {coupon.maxRedemptions ? ` / ${coupon.maxRedemptions}` : ''}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={coupon.isActive ? 'Active' : 'Inactive'}
                        size="small"
                        sx={{
                          fontFamily: "'Montserrat', sans-serif",
                          fontSize: '10px',
                          fontWeight: 600,
                          bgcolor: coupon.isActive ? 'rgba(16,185,129,0.1)' : 'rgba(156,163,175,0.15)',
                          color: coupon.isActive ? '#047857' : '#6B7280',
                          height: 22,
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '11px', color: '#6B7280' }}>
                      {coupon.validUntil
                        ? (() => {
                            try { return format(new Date(coupon.validUntil), 'MMM d, yyyy'); }
                            catch { return coupon.validUntil; }
                          })()
                        : 'No expiry'}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        sx={{
                          fontFamily: "'Montserrat', sans-serif",
                          fontSize: '11px',
                          textTransform: 'none',
                          color: '#1A5F7A',
                          minWidth: 'auto',
                        }}
                      >
                        Edit
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* A/B Tests */}
      <Paper sx={{ borderRadius: '14px', border: '1px solid #e5e7eb', mb: 3, overflow: 'hidden' }}>
        <Box sx={{ p: 2, borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography
            sx={{
              fontFamily: "'Montserrat', sans-serif",
              fontSize: '13px',
              fontWeight: 700,
              color: '#111827',
            }}
          >
            A/B Tests
          </Typography>
          <Button
            variant="outlined"
            size="small"
            sx={{
              fontFamily: "'Montserrat', sans-serif",
              fontSize: '11px',
              fontWeight: 600,
              textTransform: 'none',
              borderColor: '#D1D5DB',
              color: '#374151',
            }}
          >
            New Test
          </Button>
        </Box>
        {data.abTests.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '13px', color: '#6B7280' }}>
              No A/B tests configured. Create a test to optimize pricing and promotions.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ p: 2, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 2 }}>
            {data.abTests.map((test) => (
              <Paper
                key={test.id}
                variant="outlined"
                sx={{ p: 2, borderRadius: '10px' }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '13px', fontWeight: 700 }}>
                    {test.name}
                  </Typography>
                  <Chip
                    label={test.status}
                    size="small"
                    sx={{
                      fontFamily: "'Montserrat', sans-serif",
                      fontSize: '9px',
                      fontWeight: 600,
                      height: 20,
                      textTransform: 'uppercase',
                      bgcolor: test.status === 'running' ? 'rgba(16,185,129,0.1)' : 'rgba(156,163,175,0.15)',
                      color: test.status === 'running' ? '#047857' : '#6B7280',
                    }}
                  />
                </Box>
                {test.description && (
                  <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '11px', color: '#6B7280', mb: 1 }}>
                    {test.description}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Box>
                    <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '10px', color: '#9CA3AF', textTransform: 'uppercase' }}>
                      Impressions
                    </Typography>
                    <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '14px', fontWeight: 700 }}>
                      {safeCount(test.totalImpressions)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '10px', color: '#9CA3AF', textTransform: 'uppercase' }}>
                      Conversions
                    </Typography>
                    <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '14px', fontWeight: 700 }}>
                      {safeCount(test.totalConversions)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '10px', color: '#9CA3AF', textTransform: 'uppercase' }}>
                      CVR
                    </Typography>
                    <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '14px', fontWeight: 700, color: '#10B981' }}>
                      {test.conversionRate > 0 ? `${test.conversionRate}%` : '\u2014'}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            ))}
          </Box>
        )}
      </Paper>

      {/* Suggested Campaigns */}
      <Paper sx={{ borderRadius: '14px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
        <Box sx={{ p: 2, borderBottom: '1px solid #e5e7eb' }}>
          <Typography
            sx={{
              fontFamily: "'Montserrat', sans-serif",
              fontSize: '13px',
              fontWeight: 700,
              color: '#111827',
            }}
          >
            Suggested Campaigns
          </Typography>
        </Box>
        <Box sx={{ p: 2, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
          {data.suggestedCampaigns.map((campaign) => (
            <Paper
              key={campaign.id}
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: '10px',
                borderColor: '#E5E7EB',
                '&:hover': { borderColor: '#1A5F7A', bgcolor: 'rgba(26,95,122,0.02)' },
              }}
            >
              <Chip
                label={campaign.type}
                size="small"
                sx={{
                  fontFamily: "'Montserrat', sans-serif",
                  fontSize: '9px',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  height: 20,
                  mb: 1,
                  bgcolor: 'rgba(26,95,122,0.08)',
                  color: '#1A5F7A',
                }}
              />
              <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '13px', fontWeight: 700, color: '#111827', mb: 0.5 }}>
                {campaign.name}
              </Typography>
              <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '11px', color: '#6B7280', mb: 1 }}>
                {campaign.description}
              </Typography>
              <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontSize: '11px', fontWeight: 600, color: '#10B981' }}>
                Est. Impact: {campaign.estimatedImpact}
              </Typography>
            </Paper>
          ))}
        </Box>
      </Paper>
    </Box>
  );
};

export default PromotionsDiscountsPage;
